import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { Subscription } from 'rxjs';
import { workflowCommandIdentity } from '@app/shared/workflow-commands/workflow-command-registry';
import type { WorkflowPreviewInputChange } from '@app/workflow-workspace/workflow-workspace.component';
import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionDraftDetail,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import { mapWorkflowRegistryDiagnosticError } from './platform-workflow-registry-diagnostics.models';
import { WorkflowPresentationPreviewComponent } from './workflow-presentation-preview.component';
import type { StepAuthoringCatalog } from './workflow-step-composition';
import { WorkflowDraftDryRunService } from './workflow-draft-dry-run.service';
import type {
  DryRunAction,
  DryRunGate,
  DryRunAttempt,
  DryRunRequirementState,
  DryRunRouting,
  DryRunVisitInput,
  WorkflowDraftDryRunRequest,
  WorkflowDraftDryRunResult,
} from './workflow-draft-dry-run.models';

@Component({
  selector: 'phms-workflow-draft-dry-run',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    WorkflowPresentationPreviewComponent,
  ],
  templateUrl: './workflow-draft-dry-run.component.html',
  styleUrl: './workflow-draft-dry-run.component.scss',
})
export class WorkflowDraftDryRunComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) draft!: WorkflowDefinitionDraftDetail;
  @Input() workingDocument: WorkflowDefinitionDocument | null = null;
  @Input() unsavedSettings = false;
  @Input() unappliedChanges = false;
  @Input() sourceLoading = false;
  @Input() recoveryPending = false;
  @Input() disabled = false;
  @ViewChild(WorkflowPresentationPreviewComponent)
  private presentation?: WorkflowPresentationPreviewComponent;
  private readonly api = inject(WorkflowDraftDryRunService);
  private readonly lifecycle = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private request?: Subscription;
  private catalogRequest?: Subscription;
  private generation = 0;
  private fingerprint = '';
  private sourceDraftId: number | null = null;
  private accepted: DryRunAttempt[] = [];
  private entryRouting: DryRunRouting[] | null = null;
  private readonly visits = new Map<string, DryRunVisitInput>();
  private lastVisit = 1;
  private lastNode = '';
  useUnsaved = false;
  readOnly = false;
  loading = false;
  catalogLoading = false;
  catalog: StepAuthoringCatalog | null = null;
  catalogError = '';
  result: WorkflowDraftDryRunResult | null = null;
  error = '';
  notice = '';
  scenarioEdited = false;
  selectedNodeId = '';
  view: 'detail' | 'dashboard' = 'detail';
  dashboardId = '';
  canvasSize: 'available' | 'phone' = 'available';
  startingDataJson = '{}';
  readonly requirementStates: {
    code: DryRunRequirementState;
    label: string;
  }[] = [
    { code: 'unevaluated', label: 'Not evaluated' },
    { code: 'unmet', label: 'Missing' },
    { code: 'satisfied', label: 'Supplied in scenario' },
    { code: 'stale', label: 'Stale source' },
    { code: 'technical_failure', label: 'Technical failure' },
  ];
  readonly routeStates = [
    { code: 'missing', label: 'Not supplied' },
    { code: 'selected', label: 'Selected outcome' },
    { code: 'stale', label: 'Stale source' },
    { code: 'technical_failure', label: 'Technical failure' },
  ] as const;
  private readonly routeInputs = new Map<
    string,
    { state: DryRunRouting['state']; outcomeCode: string; touched: boolean }
  >();

  get usesUnsaved(): boolean {
    return this.useUnsaved && this.unsavedSettings && !!this.workingDocument;
  }
  get sourceDocument(): WorkflowDefinitionDocument {
    return this.usesUnsaved ? this.workingDocument! : this.draft.document;
  }
  get document(): WorkflowDefinitionDocument {
    return this.result?.document ?? this.sourceDocument;
  }
  get steps(): WorkflowDefinitionNode[] {
    return this.document.nodes.filter(
      (node) => node?.typeCode === 'human_step',
    );
  }
  get node(): WorkflowDefinitionNode | null {
    const matches = this.document.nodes.filter(
      (node) => node?.id === this.selectedNodeId,
    );
    return matches.length === 1 ? matches[0] : null;
  }
  get currentVisit(): number {
    return this.result?.current?.nodeId === this.selectedNodeId
      ? this.result.current.visitNumber
      : this.lastNode === this.selectedNodeId
        ? this.lastVisit
        : 1;
  }
  get visit(): DryRunVisitInput {
    const key = JSON.stringify([
      this.draft.id,
      this.selectedNodeId,
      this.currentVisit,
    ]);
    let input = this.visits.get(key);
    if (!input) {
      input = {
        action: '',
        inputs: {},
        outcomeCode: '',
        reason: '',
        requirements: {},
      };
      this.visits.set(key, input);
    }
    return input;
  }
  get actions(): DryRunAction[] {
    return this.result?.current?.nodeId === this.selectedNodeId
      ? this.result.current.actions
      : [];
  }
  get action(): DryRunAction | null {
    return (
      this.actions.find(
        (action) => this.identity(action) === this.visit.action,
      ) ?? null
    );
  }
  get usesOutcomeForm(): boolean {
    return (
      !!this.action &&
      ['workflow.review.submit', 'workflow.review.record'].includes(
        this.action.command.key.code,
      ) &&
      !!this.catalog?.commands.some(
        (command) =>
          workflowCommandIdentity(command.capability.key) ===
            this.visit.action && !!command.form,
      )
    );
  }
  get isCurrent(): boolean {
    return (
      !!this.result?.current &&
      this.result.current.nodeId === this.selectedNodeId
    );
  }
  get previewReadOnly(): boolean {
    return (
      this.readOnly ||
      !this.isCurrent ||
      !this.result?.current?.contractAvailable ||
      this.loading
    );
  }
  get requestBlocked(): boolean {
    return (
      this.disabled ||
      this.sourceLoading ||
      this.recoveryPending ||
      (this.usesUnsaved && this.unappliedChanges) ||
      this.loading
    );
  }
  get hasInputs(): boolean {
    return (
      this.startingDataJson.trim() !== '{}' ||
      [...this.routeInputs.values()].some((input) => input.touched) ||
      [...this.visits.values()].some(
        (visit) =>
          Object.values(visit.inputs).some(
            (input) => Object.keys(input).length > 0,
          ) ||
          !!visit.reason ||
          !!visit.outcomeCode ||
          Object.keys(visit.requirements).length > 0,
      )
    );
  }
  get recoveryCopy(): string {
    return JSON.stringify(
      {
        sampleVisits: [...this.visits].map(([key, input]) => ({
          key,
          ...input,
        })),
        routeAssumptions: Object.fromEntries(this.routeInputs),
        startingDataJson: this.startingDataJson,
      },
      null,
      2,
    );
  }
  get routingNodes(): WorkflowDefinitionNode[] {
    return this.sourceDocument.nodes.filter((node) => !!node?.routing);
  }
  get beforeDestination(): string {
    if (this.action && !this.action.completesStep)
      return 'This action keeps the current visit open.';
    if (this.action?.proposedRouteReasons.length)
      return this.action.proposedRouteReasons
        .map((reason) => reason.message)
        .join(' ');
    if (this.action?.proposedDestinationNodeId)
      return `${this.nodeLabel(this.action.proposedDestinationNodeId)}${this.action.receivingContractAvailable ? '' : ' · receiving contract unavailable'}`;
    const outcome = this.action?.outcomes.find(
      (item) => item.code === this.visit.outcomeCode,
    );
    return outcome
      ? this.nodeLabel(outcome.destinationNodeId)
      : this.action?.completesStep
        ? 'The server resolves the destination when this action is checked.'
        : 'This action keeps the current visit open.';
  }
  identity(action: Pick<DryRunAction, 'command'>): string {
    return workflowCommandIdentity(action.command.key);
  }
  nodeLabel(id: string | null): string {
    if (!id) return 'No next visit';
    const matches = this.document.nodes.filter((node) => node?.id === id);
    return matches.length === 1
      ? matches[0].displayName || matches[0].stepCode || id
      : id;
  }
  routeOutcomes(node: WorkflowDefinitionNode): string[] {
    return [
      ...new Set(
        this.sourceDocument.edges
          .filter((edge) => edge?.fromNodeId === node.id && !!edge.outcomeCode)
          .map((edge) => edge.outcomeCode!),
      ),
    ];
  }
  routeInput(node: WorkflowDefinitionNode) {
    let input = this.routeInputs.get(node.id);
    if (!input) {
      input = { state: 'missing', outcomeCode: '', touched: false };
      this.routeInputs.set(node.id, input);
    }
    return input;
  }
  routeChanged(node: WorkflowDefinitionNode): void {
    this.routeInput(node).touched = true;
    this.scenarioEdited = true;
  }
  requirementState(id: string): string {
    const evaluated = this.action?.requirements.find(
      (gate) => gate.id === id,
    )?.state;
    return Object.hasOwn(this.visit.requirements, id)
      ? this.visit.requirements[id]
      : (evaluated ?? 'unevaluated');
  }
  isAssumptionState(state: string): boolean {
    return this.requirementStates.some((choice) => choice.code === state);
  }
  requirementMessage(gate: DryRunGate): string {
    if (!Object.hasOwn(this.visit.requirements, gate.id)) return gate.message;
    const messages: Record<DryRunRequirementState, string> = {
      satisfied: 'Supplied in this scenario only. No live evidence is created.',
      unmet: 'Missing in this scenario.',
      stale: 'This scenario assumes a stale source.',
      technical_failure: 'This scenario assumes a technical failure.',
      unevaluated: 'Not evaluated in this scenario.',
    };
    return messages[this.visit.requirements[gate.id]];
  }
  setRequirement(id: string, state: DryRunRequirementState): void {
    this.visit.requirements = { ...this.visit.requirements, [id]: state };
    this.scenarioEdited = true;
  }

  ngOnChanges(): void {
    if (!this.draft) return;
    this.sourceChanged();
    if (this.sourceLoading || this.recoveryPending) {
      this.invalidate();
      this.notice =
        'The source is loading or in conflict recovery. Previous results are cleared; sample inputs are kept.';
    }
  }
  ngOnDestroy(): void {
    this.generation++;
    this.request?.unsubscribe();
    this.catalogRequest?.unsubscribe();
  }
  sourceChanged(): void {
    if (this.sourceDraftId !== this.draft.id) {
      this.visits.clear();
      this.routeInputs.clear();
      this.startingDataJson = '{}';
      this.lastNode = '';
      this.lastVisit = 1;
      this.sourceDraftId = this.draft.id;
    }
    const fingerprint = JSON.stringify([
      this.draft.id,
      this.draft.revision,
      this.draft.eTag,
      this.draft.canonicalContentHash,
      this.usesUnsaved,
      this.sourceDocument,
    ]);
    if (fingerprint === this.fingerprint) return;
    const hadReceipt = !!this.result || this.loading || !!this.accepted.length;
    this.fingerprint = fingerprint;
    this.invalidate();
    this.notice = hadReceipt
      ? 'The draft source changed. Previous simulation results are cleared; recoverable sample inputs are kept. Start a new check.'
      : '';
    if (!this.steps.some((node) => node.id === this.selectedNodeId))
      this.selectedNodeId = this.steps[0]?.id ?? '';
    this.loadCatalog();
  }
  private invalidate(): void {
    this.generation++;
    this.request?.unsubscribe();
    this.loading = false;
    this.result = null;
    this.accepted = [];
    this.entryRouting = null;
    this.scenarioEdited = false;
    this.error = '';
  }
  changeReadOnly(): void {
    this.invalidate();
    this.notice =
      'Access scenario changed. Sample inputs are kept; check the connected path again.';
  }
  startingDataChanged(): void {
    this.invalidate();
    this.notice =
      'Starting-data scenario changed. Sample form inputs are kept; check the path again.';
  }
  selectNode(): void {
    this.dashboardId = '';
  }
  selectAction(): void {
    const action = this.action;
    if (!action) return;
    this.visit.outcomeCode = String(
      this.visit.inputs[this.visit.action]?.['outcome'] ?? '',
    );
    this.presentation?.selectSampleAction(action.command);
  }
  recordInput(event: WorkflowPreviewInputChange): void {
    const identity = workflowCommandIdentity(event.command.key);
    const visit = this.visit;
    visit.inputs = {
      ...visit.inputs,
      [identity]: structuredClone(event.values),
    };
    if (this.actions.some((action) => this.identity(action) === identity))
      visit.action = identity;
    if (typeof event.values['outcome'] === 'string')
      visit.outcomeCode = event.values['outcome'];
    if (typeof event.values['notes'] === 'string')
      visit.reason = event.values['notes'];
    this.scenarioEdited = true;
  }
  private routing(): DryRunRouting[] {
    return this.routingNodes
      .filter((node) => this.routeInputs.get(node.id)?.touched)
      .map((node) => ({
        nodeId: node.id,
        conditionCode: node.routing!.conditionCode,
        conditionVersion: node.routing!.conditionVersion,
        state: this.routeInput(node).state,
        outcomeCode: this.routeInput(node).outcomeCode,
      }));
  }
  check(): void {
    this.evaluate(this.accepted);
  }
  simulateAction(): void {
    const current = this.result?.current,
      action = this.action;
    if (
      !current ||
      !this.isCurrent ||
      !action ||
      this.readOnly ||
      this.accepted.length >= 100
    )
      return;
    const visit = this.visit;
    const attempt: DryRunAttempt = {
      nodeId: current.nodeId,
      command: action.command,
      outcomeCode: visit.outcomeCode || null,
      reason: visit.reason || null,
      values: structuredClone(visit.inputs[this.identity(action)] ?? {}),
      requirements: action.requirements
        .filter((gate) => Object.hasOwn(visit.requirements, gate.id))
        .map((gate) => ({ id: gate.id, state: visit.requirements[gate.id] })),
      routing: this.routing(),
    };
    this.evaluate([...this.accepted, attempt]);
  }
  restart(): void {
    this.invalidate();
    this.lastVisit = 1;
    this.lastNode = '';
    this.notice =
      'Path restarted. Sample inputs remain available for recovery.';
    this.check();
  }
  clearInputs(): void {
    this.visits.clear();
    this.routeInputs.clear();
    this.startingDataJson = '{}';
    this.invalidate();
    this.notice = 'Sample inputs and path cleared.';
  }

  private evaluate(actions: DryRunAttempt[]): void {
    if (this.requestBlocked) return;
    let startingData: Record<string, unknown>;
    try {
      const value: unknown = JSON.parse(this.startingDataJson);
      if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error('object required');
      startingData = value as Record<string, unknown>;
    } catch {
      this.error =
        'Supply a JSON object for synthetic starting data. Your sample inputs are kept.';
      return;
    }
    const generation = ++this.generation,
      fingerprint = this.fingerprint;
    const draft = this.draft;
    const request: WorkflowDraftDryRunRequest = {
      expectedRevision: draft.revision,
      expectedContentHash: draft.canonicalContentHash,
      readOnly: this.readOnly,
      actions: structuredClone(actions),
      routing: this.entryRouting ?? this.routing(),
      currentRouting: this.routing(),
      startingData,
      ...(this.usesUnsaved
        ? { workingDocument: structuredClone(this.sourceDocument) }
        : {}),
    };
    this.request?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.notice = '';
    this.request = this.api.simulate(draft.id, draft.eTag, request).subscribe({
      next: (result) => {
        if (generation !== this.generation || fingerprint !== this.fingerprint)
          return;
        this.loading = false;
        if (
          result.contractVersion !== 1 ||
          result.previewKind !== 'connected-process' ||
          result.sideEffectFree !== true ||
          result.savedSource.draftId !== draft.id ||
          result.savedSource.workflowCode !== draft.workflowCode ||
          result.savedSource.draftCode !== draft.draftCode ||
          result.savedSource.revision !== draft.revision ||
          result.savedSource.eTag !== draft.eTag ||
          result.savedSource.contentHash !== draft.canonicalContentHash ||
          (result.source.isUnsaved
            ? !request.workingDocument
            : result.source.contentHash !== result.savedSource.contentHash)
        ) {
          this.result = null;
          this.error =
            'The simulation result does not match this exact draft source. Check again. Your sample inputs are kept.';
          return;
        }
        this.result = result;
        this.scenarioEdited = false;
        this.entryRouting ??= request.routing ?? [];
        this.accepted = request.actions.slice(
          0,
          result.trace.filter((trace) => trace.statusCode === 'simulated')
            .length,
        );
        if (result.current) {
          this.selectedNodeId = result.current.nodeId;
          this.lastNode = result.current.nodeId;
          this.lastVisit = result.current.visitNumber;
          const visit = this.visit;
          if (
            !this.actions.some(
              (action) => this.identity(action) === visit.action,
            )
          )
            visit.action = this.actions[0]
              ? this.identity(this.actions[0])
              : '';
        }
      },
      error: (error: unknown) => {
        if (generation !== this.generation || fingerprint !== this.fingerprint)
          return;
        this.loading = false;
        this.result = null;
        const failure = mapWorkflowRegistryDiagnosticError(error);
        this.error =
          failure.statusCode === 409 || failure.statusCode === 412
            ? 'The saved draft source is stale. Reload the draft before checking again. Your sample inputs are kept.'
            : `${failure.messages.join(' ')} Your sample inputs are kept; retry when the check is available.`;
        if (failure.statusCode === 401 || failure.statusCode === 403)
          this.catalog = null;
      },
    });
  }
  loadCatalog(): void {
    this.catalogRequest?.unsubscribe();
    this.catalog = null;
    this.catalogLoading = true;
    this.catalogError = '';
    const fingerprint = this.fingerprint;
    this.catalogRequest = this.lifecycle
      .getStepAuthoringCatalog(this.sourceDocument)
      .subscribe({
        next: (catalog) => {
          if (fingerprint === this.fingerprint) {
            this.catalog = catalog;
            this.catalogLoading = false;
          }
        },
        error: () => {
          if (fingerprint === this.fingerprint) {
            this.catalogLoading = false;
            this.catalogError =
              'Installed staff renderers could not be loaded. Sample inputs are kept.';
          }
        },
      });
  }
}
